import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface MeasurementRecord {
  id: string;
  customer_id: string;
  customer_name: string;
  date: string;
  measurements: {
    // Upper Body
    neck: number;
    shoulder_width: number;
    chest: number;
    waist: number;
    bicep: number;
    forearm: number;
    wrist: number;
    sleeve_length: number;
    back_length: number;
    armhole: number;
    
    // Lower Body
    hips: number;
    thigh: number;
    knee: number;
    calf: number;
    ankle: number;
    inseam: number;
    outseam: number;
    crotch: number;
    
    // Additional
    height: number;
    weight: number;
  };
  notes: string;
  photo?: string;
}

interface Customer {
  id: string;
  name: string;
  phone: string;
  photo?: string;
  total_measurements: number;
  last_measurement: string;
}

const MeasurementBook: React.FC = () => {
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [selectedMeasurement, setSelectedMeasurement] = useState<string>('');
  const [showComparison, setShowComparison] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDate, setFilterDate] = useState('all');

  // TODO: Load real customers from API
  const [customers, setCustomers] = useState<Customer[]>([]);

  // TODO: Load real measurement records from API
  const [measurementRecords, setMeasurementRecords] = useState<MeasurementRecord[]>([]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getMeasurementChange = (current: number, previous: number) => {
    const change = current - previous;
    if (change === 0) return { value: 0, color: 'text-gray-500' };
    if (change > 0) return { value: change, color: 'text-red-600' };
    return { value: Math.abs(change), color: 'text-green-600' };
  };

  const renderMeasurementCard = (record: MeasurementRecord) => (
    <div key={record.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-2xl mr-4">
              {record.photo || '📏'}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{record.customer_name}</h3>
              <p className="text-sm text-gray-600">{formatDate(record.date)}</p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setSelectedMeasurement(record.id)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              View Details
            </button>
            <button className="text-green-600 hover:text-green-800 text-sm font-medium">
              Print
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Chest:</span>
            <span className="ml-2 font-medium">{record.measurements.chest}"</span>
          </div>
          <div>
            <span className="text-gray-500">Waist:</span>
            <span className="ml-2 font-medium">{record.measurements.waist}"</span>
          </div>
          <div>
            <span className="text-gray-500">Sleeve:</span>
            <span className="ml-2 font-medium">{record.measurements.sleeve_length}"</span>
          </div>
          <div>
            <span className="text-gray-500">Inseam:</span>
            <span className="ml-2 font-medium">{record.measurements.inseam}"</span>
          </div>
        </div>

        {record.notes && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700">{record.notes}</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderDetailedMeasurement = (record: MeasurementRecord) => (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {record.customer_name} - {formatDate(record.date)}
          </h3>
          <div className="flex space-x-2">
            <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
              Edit
            </button>
            <button className="text-green-600 hover:text-green-800 text-sm font-medium">
              Print
            </button>
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Upper Body */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
              <span className="mr-2">👔</span>
              Upper Body
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Neck:</span>
                <span className="font-medium">{record.measurements.neck}"</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Shoulder Width:</span>
                <span className="font-medium">{record.measurements.shoulder_width}"</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Chest:</span>
                <span className="font-medium">{record.measurements.chest}"</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Waist:</span>
                <span className="font-medium">{record.measurements.waist}"</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Bicep:</span>
                <span className="font-medium">{record.measurements.bicep}"</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Forearm:</span>
                <span className="font-medium">{record.measurements.forearm}"</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Wrist:</span>
                <span className="font-medium">{record.measurements.wrist}"</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Sleeve Length:</span>
                <span className="font-medium">{record.measurements.sleeve_length}"</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Back Length:</span>
                <span className="font-medium">{record.measurements.back_length}"</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Armhole:</span>
                <span className="font-medium">{record.measurements.armhole}"</span>
              </div>
            </div>
          </div>

          {/* Lower Body */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
              <span className="mr-2">👖</span>
              Lower Body
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Hips:</span>
                <span className="font-medium">{record.measurements.hips}"</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Thigh:</span>
                <span className="font-medium">{record.measurements.thigh}"</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Knee:</span>
                <span className="font-medium">{record.measurements.knee}"</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Calf:</span>
                <span className="font-medium">{record.measurements.calf}"</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Ankle:</span>
                <span className="font-medium">{record.measurements.ankle}"</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Inseam:</span>
                <span className="font-medium">{record.measurements.inseam}"</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Outseam:</span>
                <span className="font-medium">{record.measurements.outseam}"</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Crotch:</span>
                <span className="font-medium">{record.measurements.crotch}"</span>
              </div>
            </div>
          </div>

          {/* Additional */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
              <span className="mr-2">📏</span>
              Additional
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Height:</span>
                <span className="font-medium">{record.measurements.height}"</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Weight:</span>
                <span className="font-medium">{record.measurements.weight} lbs</span>
              </div>
            </div>

            {record.notes && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-2">Notes</h5>
                <p className="text-sm text-gray-700">{record.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderComparison = () => {
    const customerRecords = measurementRecords.filter(r => r.customer_id === selectedCustomer);
    if (customerRecords.length < 2) return null;

    const sortedRecords = customerRecords.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    const latest = sortedRecords[0];
    const previous = sortedRecords[1];

    return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <span className="mr-2">📊</span>
            Measurement Comparison
          </h3>
          <p className="text-sm text-gray-600">
            {latest.customer_name} - {formatDate(latest.date)} vs {formatDate(previous.date)}
          </p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Upper Body Comparison */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Upper Body Changes</h4>
              <div className="space-y-2 text-sm">
                {Object.entries(latest.measurements).slice(0, 10).map(([key, value]) => {
                  const prevValue = previous.measurements[key as keyof typeof previous.measurements];
                  const change = getMeasurementChange(value, prevValue);
                  return (
                    <div key={key} className="flex justify-between items-center">
                      <span className="text-gray-600 capitalize">{key.replace('_', ' ')}:</span>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{value}"</span>
                        {change.value !== 0 && (
                          <span className={`text-xs ${change.color}`}>
                            {change.value > 0 ? '+' : '-'}{change.value.toFixed(1)}"
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Lower Body Comparison */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Lower Body Changes</h4>
              <div className="space-y-2 text-sm">
                {Object.entries(latest.measurements).slice(10, 18).map(([key, value]) => {
                  const prevValue = previous.measurements[key as keyof typeof previous.measurements];
                  const change = getMeasurementChange(value, prevValue);
                  return (
                    <div key={key} className="flex justify-between items-center">
                      <span className="text-gray-600 capitalize">{key.replace('_', ' ')}:</span>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{value}"</span>
                        {change.value !== 0 && (
                          <span className={`text-xs ${change.color}`}>
                            {change.value > 0 ? '+' : '-'}{change.value.toFixed(1)}"
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Summary */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Summary</h4>
              <div className="space-y-3">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="text-sm font-medium text-blue-900">Latest Measurement</div>
                  <div className="text-xs text-blue-700">{formatDate(latest.date)}</div>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <div className="text-sm font-medium text-green-900">Previous Measurement</div>
                  <div className="text-xs text-green-700">{formatDate(previous.date)}</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-900">Time Between</div>
                  <div className="text-xs text-gray-700">
                    {Math.ceil((new Date(latest.date).getTime() - new Date(previous.date).getTime()) / (1000 * 60 * 60 * 24))} days
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/tailor-dashboard" className="text-gray-400 hover:text-gray-600 mr-4">
                <span className="text-2xl">←</span>
              </Link>
              <div className="flex items-center justify-center w-10 h-10 bg-blue-600 rounded-lg">
                <span className="text-white text-lg font-bold">VT</span>
              </div>
              <span className="ml-3 text-xl font-bold text-gray-900">Measurement Book</span>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowComparison(!showComparison)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="text-2xl">📊</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search Customers</label>
                <input
                  type="text"
                  placeholder="Search by name or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Customer</label>
                <select
                  value={selectedCustomer}
                  onChange={(e) => setSelectedCustomer(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Customers</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} ({customer.total_measurements} measurements)
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date Filter</label>
                <select
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Time</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="year">This Year</option>
                </select>
              </div>
              <div className="flex items-end">
                <Link
                  to="/tailor/add-customer"
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-center"
                >
                  Add New Measurement
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Summary */}
        {selectedCustomer && (
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-3xl mr-4">
                    {customers.find(c => c.id === selectedCustomer)?.photo || '👤'}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {customers.find(c => c.id === selectedCustomer)?.name}
                    </h3>
                    <p className="text-gray-600">
                      {customers.find(c => c.id === selectedCustomer)?.phone}
                    </p>
                    <p className="text-sm text-gray-500">
                      {customers.find(c => c.id === selectedCustomer)?.total_measurements} measurements
                    </p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowComparison(!showComparison)}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    {showComparison ? 'Hide' : 'Show'} Comparison
                  </button>
                  <Link
                    to="/tailor/add-customer"
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Add Measurement
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Comparison View */}
        {showComparison && selectedCustomer && renderComparison()}

        {/* Detailed Measurement View */}
        {selectedMeasurement && (
          <div className="mb-6">
            {renderDetailedMeasurement(measurementRecords.find(r => r.id === selectedMeasurement)!)}
          </div>
        )}

        {/* Measurement Records */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Measurement Records</h2>
            <span className="text-sm text-gray-500">
              {measurementRecords.filter(r => 
                (!selectedCustomer || r.customer_id === selectedCustomer) &&
                (!searchQuery || r.customer_name.toLowerCase().includes(searchQuery.toLowerCase()))
              ).length} records
            </span>
          </div>
          
          <div className="space-y-4">
            {measurementRecords
              .filter(r => 
                (!selectedCustomer || r.customer_id === selectedCustomer) &&
                (!searchQuery || r.customer_name.toLowerCase().includes(searchQuery.toLowerCase()))
              )
              .map(renderMeasurementCard)
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeasurementBook; 